import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePermissionsContext } from '@/app/contexts/permissions';
import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { IntlContext } from '@/i18n';

import { ICard, ICardContext } from '../types';

import { createCarsCards } from './createCarsCards';
import { createDriverBlockedCard } from './createDriverBlockedCard';
import { createDriverFailedTestsCard } from './createDriverFailedTestsCard';
import { createPartnerCards } from './createPartnerCards';
import { useAcademyCard } from './useAcademyCards';
import { createDriversCards } from './useDriverCard';
import { useMobileAppCard } from './useMobileAppCard';
import { useReferralCard } from './useReferralCard';
import { useRegionSpecificCards } from './useRegionSpecificCards/useRegionSpecificCards';
import { createTestCards } from './useTestCard';

interface IParams {
  selectToDoListData: (state: any) => ICardContext;
  onNavigate: (uri: string) => void;
}

const compareByOrderValue = (card1: ICard, card2: ICard) => (card1.order || 0) - (card2.order || 0);

const useCards = ({ onNavigate, selectToDoListData }: IParams) => {
  const todoListData = useSelector(selectToDoListData);
  const intl = useIntl();
  const links = useExternalLinks();
  const { language } = useContext(IntlContext);

  const { user } = usePermissionsContext();

  const academyCards = useAcademyCard(todoListData, intl);
  const regionSpecificCards = useRegionSpecificCards(todoListData);
  const referralCard = useReferralCard();
  const downloadAppCard = useMobileAppCard();

  return useMemo((): ICard[] => {
    let result = [
      ...(user.isChauffeur ? [] : createPartnerCards(todoListData, onNavigate, intl)),
      ...(user.isChauffeur ? [] : createCarsCards(todoListData, onNavigate, intl, user, links)),
      ...(user.isChauffeur ? [] : createDriversCards(todoListData, onNavigate, intl, user)),
      ...createTestCards(todoListData, intl, language),
      ...academyCards,
      {
        id: 'reg',
        cardStatus: 'done' as const,
        title: intl.formatMessage({
          defaultMessage: 'Apply for registration',
          id: 'jFKnN2',
          description: 'todo list card text',
        }),
        desc: '',
      },
      ...regionSpecificCards,
    ].sort(compareByOrderValue);

    if (user.isChauffeur) {
      if (todoListData.myDriver?.accessStatus === 'blocked') {
        result.unshift(createDriverBlockedCard(links, intl));
      }
    } else if (todoListData.myDriver?.accessStatus === 'failed_tests') {
      result.unshift(createDriverFailedTestsCard(intl));
    } else if (todoListData.partner.accessStatus === 'active') {
      result = result.filter(card => !card.isHiddenIfPartnerIsActive);
      result.unshift(...downloadAppCard);

      if (referralCard) {
        result.unshift(referralCard);
      }
    }

    return result;
  }, [
    academyCards,
    downloadAppCard,
    intl,
    language,
    links,
    onNavigate,
    regionSpecificCards,
    todoListData,
    user,
    referralCard,
  ]);
};

export { useCards };
